<template>
  <div class="rd-screen">
    <report-tools :copy="rows.length > 0" @copy="copyTable"></report-tools>

    <report-state v-if="busy" kind="loading"></report-state>
    <report-state v-else-if="!rows.length" icon="mdi-bowl-mix-outline" :title="emptyTitle" :text="emptyText"></report-state>

    <div v-else class="rd-card">
      <table class="rd-tbl">
        <thead>
          <tr>
            <th scope="col" class="rd-col-num">#</th>
            <th scope="col" :aria-sort="ariaSort('name')">
              <button type="button" @click="sortBy('name')">{{ $t("meal") }} {{ sortArrow("name") }}</button>
            </th>
            <th scope="col">{{ $t("type") }}</th>
            <th
              v-for="shift in visibleShifts"
              :key="shift.id"
              scope="col"
              class="is-right"
              :aria-sort="ariaSort(shift.key)"
            >
              <button type="button" @click="sortBy(shift.key)">
                <report-shift-tag :shift="shift.id"></report-shift-tag>
                {{ sortArrow(shift.key) }}
              </button>
            </th>
            <th scope="col" class="is-right" :aria-sort="ariaSort('total')">
              <button type="button" @click="sortBy('total')">{{ $t("total") }} {{ sortArrow("total") }}</button>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, index) in rows" :key="row.key">
            <td class="rd-num rd-muted">{{ index + 1 }}</td>
            <td class="is-name" :title="row.name">{{ row.name }}</td>
            <td class="rd-muted">{{ row.type || "–" }}</td>
            <td v-for="shift in visibleShifts" :key="shift.id" class="is-right rd-num" :class="{ 'is-zero': !row[shift.key] }">
              {{ row.text[shift.key] }}
            </td>
            <td class="is-right rd-num"><b>{{ row.total }}</b></td>
          </tr>
        </tbody>
        <tfoot>
          <tr>
            <td></td>
            <td>{{ $t("total") }}</td>
            <td></td>
            <td v-for="shift in visibleShifts" :key="shift.id" class="is-right rd-num">{{ sums.text[shift.key] }}</td>
            <td class="is-right rd-num">{{ sums.total }}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
</template>

<script>
import CompanyMultiRestaurantTabMeals from "@/components/company/reports/multi-restaurant-tabs/CompanyMultiRestaurantTabMeals.vue";
import { SHIFTS } from "@/constants/shifts";
import ReportShiftTag from "@/components/report/shared/ReportShiftTag.vue";
import { copyText, rowsToTsv } from "@/components/report/desktop/copyTable";
import hasTableSort from "@/components/report/desktop/hasTableSort";
import ReportState from "@/components/report/desktop/ReportState.vue";
import ReportTools from "@/components/report/desktop/ReportTools.vue";
import hasMultiFeed from "../../../hasMultiFeed";

/**
 * Obroci firme "više restorana" na računaru: obrok × smena (kupci + restoran, "5 + 2R" = 5 kupaca i 2 restoranu),
 * zbir za izabrane restorane. Restorani, smene, period i pretragu čita iz zajedničke trake. Nasleđuje učitavanje,
 * štampu i Excel iz starog taba.
 */
export default {
  name: "CompanyDesktopMultiMeals",
  extends: CompanyMultiRestaurantTabMeals,
  mixins: [hasMultiFeed, hasTableSort],
  components: { ReportShiftTag, ReportState, ReportTools },
  data: () => ({
    sort: { key: "total", dir: -1 },
  }),
  computed: {
    busy() {
      return this.isLoading;
    },
    hasRows() {
      return this.rows.length > 0;
    },
    /** Izabrane smene; četvrta samo kad neki obrok u njoj ima količinu. */
    visibleShifts() {
      return SHIFTS.filter(
        (shift) =>
          this.shiftsWithData.includes(shift.id) &&
          (shift.id < 4 || this.meals.some((meal) => (meal[shift.key] && meal[shift.key].totalQuantity) > 0))
      );
    },
    /** Obroci posle pretrage (naziv ili tip). */
    found() {
      const search = this.$store.state.report.filters.search;
      return search ? this.customSearch(this.meals, [{ value: "name" }, { value: "type" }], search) : this.meals;
    },
    rows() {
      return this.sortRows(
        this.found.map((meal, index) => {
          const row = {
            key: `${meal.id || meal.name}-${index}`,
            name: meal.name,
            type: this.typeText(meal.type),
            total: meal.total || 0,
            text: {},
          };
          SHIFTS.forEach((shift) => {
            row[shift.key] = (meal[shift.key] && meal[shift.key].totalQuantity) || 0;
            row.text[shift.key] = this.formatShiftQuantity(meal[shift.key]);
          });
          return row;
        })
      );
    },
    sums() {
      const sums = { total: 0, text: {} };
      SHIFTS.forEach((shift) => {
        const sum = { customerQuantity: 0, restaurantQuantity: 0, totalQuantity: 0 };
        this.found.forEach((meal) => {
          const data = meal[shift.key] || {};
          sum.customerQuantity += data.customerQuantity || 0;
          sum.restaurantQuantity += data.restaurantQuantity || 0;
          sum.totalQuantity += data.totalQuantity || 0;
        });
        sums.text[shift.key] = this.formatShiftQuantity(sum);
      });
      sums.total = this.found.reduce((sum, meal) => sum + (meal.total || 0), 0);
      return sums;
    },
    emptyTitle() {
      const search = this.$store.state.report.filters.search;
      return search ? this.$t("report_mobile.no_results_for", { search }) : this.$t("report_mobile.no_meals");
    },
    emptyText() {
      return this.$store.state.report.filters.search ? "" : this.$t("report_desktop.empty_hint");
    },
    kpis() {
      return [
        { key: "total", label: this.$t("report_desktop.total_meals"), value: this.sums.total, tone: "inverse" },
        { key: "meals", label: this.$t("meals"), value: this.rows.length },
      ];
    },
  },
  methods: {
    /** Tip obroka iz izveštaja ("package", "meal", "food") prevedeno; nepoznat tip ostaje kakav je. */
    typeText(type) {
      return ["package", "meal", "food"].includes(type) ? this.$t(type) : type || "";
    },
    async copyTable() {
      const headers = [this.$t("meal"), this.$t("type"), ...this.visibleShifts.map((shift) => this.$t(shift.label)), this.$t("total")];
      const rows = this.rows.map((row) => [row.name, row.type, ...this.visibleShifts.map((shift) => row.text[shift.key]), row.total]);
      const copied = await copyText(rowsToTsv(headers, rows));
      this.$emit("toast", this.$t(copied ? "report_desktop.copied" : "report_desktop.copy_failed"));
    },
  },
};
</script>
